import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription, from, of } from 'rxjs';
import { switchMap, map, catchError, scan, filter, tap } from 'rxjs/operators';
import { BuildDetails } from '../../shared/model/base-build-details';
import { BaseBuildDetailsHttpService } from './base-build-details.http.service';
import { FETCH_BUILD_DETAILS_TIME } from '../constants/base-time.const';

type PollResult = { success: true; data: BuildDetails; error: null } | { success: false; data: null; error: any };
const RELOAD_PENDING_KEY = 'needsImmediateReload';

interface ScanAccumulator {
  errorCount: number;
  lastResult: PollResult;
}

@Injectable({ providedIn: 'root' })
export class BaseUpToDateBuildService {
  private currentBuildDetails$ = new BehaviorSubject<BuildDetails | null>(null);
  private upToDateSubject$ = new BehaviorSubject<boolean>(true);
  private reloadRequiredSubject$ = new BehaviorSubject<boolean>(false);

  readonly buildIsUpToDate$ = this.upToDateSubject$.asObservable();
  readonly isReloadRequired$ = this.reloadRequiredSubject$.asObservable();

  private pollingSub: Subscription | null = null;
  private MAX_CONSECUTIVE_ERRORS = 3;
  private BACKOFF_MS = 60_000;

  constructor(private httpService: BaseBuildDetailsHttpService) {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && sessionStorage.getItem(RELOAD_PENDING_KEY) === '1') {
        sessionStorage.removeItem(RELOAD_PENDING_KEY);
        window.location.reload();
      }
    });
  }

  async init(): Promise<void> {
    try {
      const initial = await this.httpService.fetchBuildDetails();
      this.currentBuildDetails$.next(initial);
    } catch (err) {
      console.warn('[UpToDateBuildService] initial fetch failed:', err);
    } finally {
      this.startPolling();
    }
  }

  private notifyNewBuild(newDetails: BuildDetails): void {
    this.currentBuildDetails$.next(newDetails);

    if (document.hidden) {
      sessionStorage.setItem(RELOAD_PENDING_KEY, '1');
      window.location.reload();
      return;
    }

    this.upToDateSubject$.next(false);
  }

  private startPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }

    this.pollingSub = interval(FETCH_BUILD_DETAILS_TIME)
      .pipe(
        switchMap(() =>
          from(this.httpService.fetchBuildDetails()).pipe(
            map<BuildDetails, PollResult>((data) => ({ success: true, data, error: null })),
            catchError((err) => {
              console.warn('[UpToDateBuildService] fetch error', err);
              return of<PollResult>({ success: false, data: null, error: err });
            })
          )
        ),
        scan<PollResult, ScanAccumulator>(
          (acc, result) => ({
            errorCount: result.success ? 0 : acc.errorCount + 1,
            lastResult: result,
          }),
          { errorCount: 0, lastResult: { success: true, data: null as any, error: null } }
        ),

        tap((acc) => {
          if (acc.errorCount >= this.MAX_CONSECUTIVE_ERRORS) {
            console.error(
              `[UpToDateBuildService] ${acc.errorCount} consecutive errors – ` +
                `pausing polling for ${this.BACKOFF_MS / 1000}s`
            );
            this.stopPolling();
            setTimeout(() => this.startPolling(), this.BACKOFF_MS);
          }
        }),
        filter((acc) => acc.lastResult.success),
        map((acc) => acc.lastResult.data as BuildDetails)
      )
      .subscribe({
        next: (buildDetails) => {
          const oldInfo = this.currentBuildDetails$.value;
          if (!oldInfo || oldInfo.buildNumber !== buildDetails.buildNumber) {
            this.notifyNewBuild(buildDetails);
          }
        },
        error: (err) => {
          console.error('[UpToDateBuildService] polling stream terminated', err);
          setTimeout(() => this.startPolling(), this.BACKOFF_MS);
        },
      });
  }

  stopPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  setIsReloadRequired(value: boolean): void {
    this.reloadRequiredSubject$.next(value);
  }

  get currentBuildDetails(): BuildDetails | null {
    return this.currentBuildDetails$.value;
  }
}
